import { useMemo, useState } from 'react';
import { Link } from '@elementary/common-ui/components';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { ActivityLogsIntegration } from 'shared/types';
import { parseIntegrationError } from 'settings/utils';
import multipleDestinationsRenderer from 'settings/components/shared/multipleDestinationsRenderer';
import IntegrationBox from '../../shared/Integration/IntegrationBox';
import ActivityLogsIntegrationDialog from './ActivityLogsIntegrationDialog';
import DeleteActivityLogsIntegrationDialog from './DeleteActivityLogsIntegrationDialog';
import {
  ACTIVITY_LOGS_INTEGRATION_TYPES,
  ActivityLogsIntegrationFormValues,
  getActivityLogsIntegrationIcon,
  toCreateActivityLogsIntegrationPayload,
  toUpdateActivityLogsIntegrationPayload,
} from './activityLogsIntegrationUtils';

type Props = {
  account?: { activity_logs_enabled?: boolean };
  supportChat: { show: () => void };
};

const EMPTY_INTEGRATIONS: ActivityLogsIntegration[] = [];

const ExternalIntegrationsSection = ({ account, supportChat }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const hasAccountPermission = useAccountPermission();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const canViewIntegrations = hasAccountPermission('VIEW_INTEGRATIONS');
  const canEditIntegrations = hasAccountPermission('EDIT_INTEGRATIONS');

  const integrationsQuery = useQuery(
    ['activityLogsIntegrations'],
    () => api.fetchActivityLogsIntegrations(),
    { enabled: canViewIntegrations },
  );

  const integrations =
    integrationsQuery.data?.integrations ?? EMPTY_INTEGRATIONS;

  const selectedIntegration = useMemo(
    () => integrations.find((i) => i.id === selectedIntegrationId),
    [integrations, selectedIntegrationId],
  );

  const upsertMutation = useMutation(
    async (values: ActivityLogsIntegrationFormValues) => {
      if (!values.id) {
        return api.createActivityLogsIntegration(
          toCreateActivityLogsIntegrationPayload(values),
        );
      }

      return api.updateActivityLogsIntegration({
        id: values.id,
        data: toUpdateActivityLogsIntegrationPayload(values),
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['activityLogsIntegrations']);
        openSnackbar({
          message: 'Successfully connected to your external integration',
          variant: 'success',
        });
        setIsDialogOpen(false);
        setSelectedIntegrationId(null);
      },
      onError: (error: any) => {
        openSnackbar({
          message:
            (error?.message as string) ||
            'Failed to connect external integration',
          variant: 'error',
        });
      },
    },
  );

  const deleteMutation = useMutation(
    async (id: string) => api.deleteActivityLogsIntegration(id),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['activityLogsIntegrations']);
        openSnackbar({
          message: 'Successfully disconnected from your external integration',
          variant: 'success',
        });
        setIsDeleteDialogOpen(false);
        setSelectedIntegrationId(null);
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to disconnect from your external integration',
          variant: 'error',
        });
      },
    },
  );

  const integrationsList = useMemo(
    () =>
      integrations.map((integration) => ({
        id: integration.id,
        icon: getActivityLogsIntegrationIcon(integration.type) || null,
        name: integration.name,
        status: 'connected',
      })),
    [integrations],
  );

  const isPlanEnabled = !!account?.activity_logs_enabled;
  const canConnect =
    isPlanEnabled && canEditIntegrations && canViewIntegrations;
  const tooltipTitle = !isPlanEnabled ? (
    <span>
      Activity logs are available on higher-tier plans.{' '}
      <Link onClick={supportChat.show} href="#">
        Contact us
      </Link>{' '}
      to learn more.
    </span>
  ) : !canViewIntegrations ? (
    <span>You do not have permission to view integrations.</span>
  ) : !canEditIntegrations ? (
    <span>You do not have permission to edit integrations.</span>
  ) : undefined;

  return (
    <>
      <IntegrationBox
        title="External Integrations"
        description="Send activity logs and system logs to external systems."
        addText="Add new external integration"
        connectTooltipTitle={tooltipTitle}
        onOpen={() => {
          setSelectedIntegrationId(null);
          setIsDialogOpen(true);
        }}
        connected={!!integrations.length}
        disabled={!canConnect}
        loading={integrationsQuery.isLoading}
        supportMultipleDestinations={!!integrations.length}
        multipleDestinationsRenderer={() =>
          multipleDestinationsRenderer({
            integrations: integrationsList,
            onChange: (method: string, id: string) => {
              setSelectedIntegrationId(id);
              if (method === 'update') {
                setIsDialogOpen(true);
              } else if (method === 'delete') {
                setIsDeleteDialogOpen(true);
              }
            },
            options: [
              { value: 'update', label: 'Edit connection' },
              { value: 'delete', label: 'Delete connection', color: 'red' },
            ],
          })
        }
        onMultipleDestinationsConnectClick={() => {
          setSelectedIntegrationId(null);
          setIsDialogOpen(true);
        }}
      />

      <ActivityLogsIntegrationDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={(values) => upsertMutation.mutate(values)}
        supportedIntegrationTypes={ACTIVITY_LOGS_INTEGRATION_TYPES}
        error={parseIntegrationError(upsertMutation.error)}
        isLoading={upsertMutation.isLoading}
        data={selectedIntegration}
      />

      <DeleteActivityLogsIntegrationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        selectedIntegration={selectedIntegration}
        onSubmit={(id) => deleteMutation.mutate(id)}
        loading={deleteMutation.isLoading}
      />
    </>
  );
};

export default ExternalIntegrationsSection;
